export enum PusherEventType {
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  ANALYTICS_CONSENT = 'analytics-consent',
  CLEVERTAP_EVENT = 'clevertap-event'
}

interface AnalyticsConsentData {
  consentGiven: boolean;
}

interface CleverTapEventData {
  name: string;
  props?: Record<string, any>;
}

class PusherService {
  private static instance: PusherService | null = null;
  private currentUserId: string | null = null;

  private constructor() {
    // Singleton pattern
  }

  static getInstance(): PusherService {
    if (!PusherService.instance) {
      PusherService.instance = new PusherService();
    }
    return PusherService.instance;
  }

  initialize(userId: string): void {
    this.currentUserId = userId;
  }

  private async _publishEvent(
    eventType: PusherEventType,
    data?: any
  ): Promise<boolean> {
    if (!this.currentUserId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[Pusher] No user ID set. Cannot publish event:',
          eventType
        );
      }
      return false;
    }

    const channel = `private-user-${this.currentUserId}`;

    try {
      const response = await fetch('/api/pusher/publish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          channel,
          event: eventType,
          data: data || {}
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Pusher] Error publishing event:', error);
      }
      return false;
    }
  }

  async publishLoginSuccess(): Promise<boolean> {
    return this._publishEvent(PusherEventType.LOGIN_SUCCESS);
  }

  async publishLoginFailed(): Promise<boolean> {
    return this._publishEvent(PusherEventType.LOGIN_FAILED);
  }

  async publishAnalyticsConsent(consentGiven: boolean): Promise<boolean> {
    const data: AnalyticsConsentData = { consentGiven };
    return this._publishEvent(PusherEventType.ANALYTICS_CONSENT, data);
  }

  async publishCleverTapEvent(
    name: string,
    props?: Record<string, any>
  ): Promise<boolean> {
    const data: CleverTapEventData = { name, props };
    return this._publishEvent(PusherEventType.CLEVERTAP_EVENT, data);
  }

  // Public method for testing
  async publishEvent(
    eventType: PusherEventType | string,
    data?: any
  ): Promise<boolean> {
    return this._publishEvent(eventType as PusherEventType, data);
  }
}

export const pusherService = PusherService.getInstance();

export const initializePusher = (userId: string): void => {
  pusherService.initialize(userId);
};

export const publishLoginSuccess = (): Promise<boolean> =>
  pusherService.publishLoginSuccess();

export const publishLoginFailed = (): Promise<boolean> =>
  pusherService.publishLoginFailed();

export const publishAnalyticsConsent = (
  consentGiven: boolean
): Promise<boolean> => pusherService.publishAnalyticsConsent(consentGiven);

export const publishCleverTapEvent = (
  name: string,
  props?: Record<string, any>
): Promise<boolean> => pusherService.publishCleverTapEvent(name, props);
